import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Wifi, ShieldCheck, Smartphone, CreditCard, Zap, FileSearch } from "lucide-react";
import ContactForm from "@/components/ContactForm";
import RelatedReviews from "@/components/RelatedReviews";
import LocationCrossLinks from "@/components/LocationCrossLinks";
import FormReviewCompanion from "@/components/FormReviewCompanion";
import Breadcrumbs from "@/components/Breadcrumbs";
import SocialProofStrip from "@/components/SocialProofStrip";
import ResponsiveAccordion from "@/components/ResponsiveAccordion";
import ProductComparisonMatrix from "@/components/product-pages/ProductComparisonMatrix";
import { useProductViewed } from "@/utils/trackingHooks";
import analytics from "@/utils/analytics";
import SEOHead from "@/components/SEOHead";
import WhatsAppButton from "@/components/WhatsAppButton";
import { fadeUp, stagger, viewportOnce } from "@/utils/motionVariants";

const PROD_URL = process.env.REACT_APP_PRODUCTION_URL || "https://candopayments.com";

// `slug` links a card to its dedicated /card-machines/<slug> product page.
// `cover: true` marks the 1920x1080 studio renders — they get a centre crop
// (object-cover) inside the square tile so the device fills it; transparent
// cutouts keep object-contain. `tags` drive the quick filter chips.
const MACHINES = [
  { name: "PAX A920 Pro Smart", slug: "pax-a920-pro", img: "https://cdn.candopayments.com/products/card-machines/pax-a920-pro/a920-pro-front-view_colour_web_transparent.png", tags: ["portable", "smart"], features: ["WiFi & 4G connectivity", "App-based business tools", "Fast & secure transactions", "Staff logins & controls"] },
  { name: "Ingenico DX8000", slug: "ingenico-dx8000", img: "https://cdn.candopayments.com/products/card-machines/ingenico/dx-8000-front-view_colour_web_solid.png", cover: true, tags: ["portable", "smart"], features: ["Touchscreen display", "Serve customers anywhere", "Reliable coverage", "Same till-point security"] },
  { name: "Ingenico Move 5000", slug: "ingenico-move-5000", img: "https://cdn.candopayments.com/products/card-machines/ingenico/move-5000-front-view-screen_colour_web_solid.png", cover: true, tags: ["portable"], features: ["Portable design", "Easy touchscreen", "Reliable coverage", "Fast transactions"] },
  { name: "Ingenico Desk 5000", slug: "ingenico-desk-5000", img: "https://cdn.candopayments.com/products/card-machines/ingenico/desk-5000-side-angle-hero-right_colour_web_solid.png", cover: true, tags: ["countertop"], features: ["Robust countertop device", "Best value for money", "Easy till integration", "Hygienic PIN pad"] },
  { name: "Verifone T650P", slug: "verifone-t650p", img: "https://cdn.candopayments.com/products/card-machines/verifone/t650c-front_colour_web_transparent.png", tags: ["portable", "smart"], features: ["All payment types", "Mobile payments", "EPOS integrations", "Portable design"] },
  { name: "Zettle by PayPal", slug: "paypal-reader", img: "https://cdn.candopayments.com/products/card-machines/zettle/zettle_colour_web_transparent.png", tags: ["pocket"], features: ["Bluetooth connection", "Speedy setup", "Start selling instantly", "All-day battery"] },
  { name: "Elavon Mobile", slug: "elavon-mobile", img: "https://cdn.candopayments.com/products/card-machines/elavon/elavon-mobile-front-view-screen_colour_web_solid.webp", cover: true, tags: ["pocket"], features: ["Just £19.99 for reader", "Rates from 1.75%", "No authorisation fee", "No monthly payment"] },
];

const FILTERS = [
  { key: "all", label: "All machines" },
  { key: "smart", label: "Smart Android" },
  { key: "portable", label: "Portable" },
  { key: "countertop", label: "Countertop" },
  { key: "pocket", label: "Pocket readers" },
];

// Hub-level FAQs. Passed to SEOHead as-is, so the FAQPage JSON-LD always
// matches the visible accordion. Worded to complement (not duplicate) the
// per-product FAQ sets on the sub-pages.
const HUB_FAQS = [
  {
    q: "Which card machine is best for a small business in the UK?",
    a: "It depends where you take payments. Fixed tills suit a countertop terminal like the Ingenico Desk 5000; table service and queue busting suit a portable like the Move 5000; occasional or on-the-road payments suit a pocket reader like Elavon Mobile. Tell CanDo how you trade and we'll recommend the right fit.",
  },
  {
    q: "What is the difference between portable and countertop card machines?",
    a: "A countertop card machine stays wired at a fixed point, giving maximum stability at a till or reception. A portable card machine connects over WiFi, Bluetooth or 4G so staff can take payment at the table, on the shop floor or anywhere in the venue.",
  },
  {
    q: "Do all CanDo card machines take Apple Pay and Google Pay?",
    a: "Yes. Every card machine we supply accepts Apple Pay, Google Pay and contactless cards, alongside chip & PIN payments.",
  },
  {
    q: "How quickly can I get a card machine from CanDo?",
    a: "Order before 2pm and your terminal can be live the next working day. Setup and switching are handled by our UK-based team.",
  },
  {
    q: "Can CanDo help me switch from my current provider?",
    a: "Yes. Send us a recent statement and we'll review it for free, show you exactly what you'd save, and manage the switch end to end — including matching or beating your current rates where we can.",
  },
  {
    q: "Do I need an EPOS system to use a card machine?",
    a: "No. Every machine works standalone. If you do run an EPOS system, most of our terminals offer till integration so totals stay accurate and end-of-day reconciliation is quicker.",
  },
];

// ItemList schema so search engines understand the grid as a product catalogue.
const ITEM_LIST_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "CanDo Payments card machine range",
  numberOfItems: MACHINES.length,
  itemListElement: MACHINES.map((m, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: m.name,
    url: `${PROD_URL}/card-machines/${m.slug}`,
  })),
};

const machineHeroInitial = { opacity: 0, scale: 0.95 };
const machineHeroAnimate = { opacity: 1, scale: 1 };
const machineHeroTransition = { duration: 0.5, delay: 0.2 };

function CardMachinesHero() {
  return (
    <section className="hero-gradient py-16 md:py-24" data-testid="card-machines-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <Breadcrumbs className="mb-5" />
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Card Payment Machines</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">
              The right card machine for <span className="text-brand-aqua">every business</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">
              Whether you need a mobile reader for market days or a countertop terminal for your shop, we match you with the right machine, the right pricing, and the right support. Order before 2pm and your terminal can be live the next working day.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
              <a href="#machines" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="card-machines-cta-browse">
                Browse Machines <ArrowRight size={16} />
              </a>
              <Link to="/quote/in-person" className="bg-brand-pink-light text-brand-pink hover:bg-brand-pink/15 border border-brand-pink/30 font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="card-machines-cta-estimate">
                Get an Instant Estimate
              </Link>
              <WhatsAppButton variant="secondary" placement="card_machines" label="WhatsApp us" />
            </motion.div>
          </motion.div>
          <motion.div initial={machineHeroInitial} animate={machineHeroAnimate} transition={machineHeroTransition} className="hidden lg:flex items-center justify-center gap-4">
            <img src="https://cdn.candopayments.com/products/card-machines/pax-a920-pro/a920-pro-front-view_colour_web_transparent.png" alt="PAX A920 Pro" width="369" height="862" className="h-56 w-auto object-contain drop-shadow-xl float-animation" />
            <img src="https://cdn.candopayments.com/products/card-machines/ingenico/move-5000_colour_web_transparent.png" alt="Ingenico Move 5000" width="1080" height="1080" className="h-48 w-auto object-contain drop-shadow-lg float-animation" style={{ animationDelay: '1s' }} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function MachineGrid() {
  const [filter, setFilter] = useState("all");
  const visible = filter === "all" ? MACHINES : MACHINES.filter((m) => m.tags.includes(filter));

  return (
    <section id="machines" className="py-20 md:py-28 bg-gray-50" data-testid="machines-grid">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Quick filter chips — lightweight "help me choose" without a wizard. */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="machine-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-full px-5 py-2 text-sm font-semibold border transition-colors ${
                filter === f.key
                  ? "bg-brand-aqua text-white border-brand-aqua"
                  : "bg-white text-brand-grey border-brand-light-grey hover:border-brand-aqua hover:text-brand-aqua"
              }`}
              data-testid={`machine-filter-${f.key}`}
              aria-pressed={filter === f.key}
            >
              {f.label}
            </button>
          ))}
        </div>
        <motion.div key={filter} initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {visible.map((m) => (
            <motion.div key={m.name} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col" data-testid={`machine-card-${m.name.toLowerCase().replace(/\s+/g, '-')}`}>
              {/* Whole visual + title is the details link; one primary CTA below. */}
              <Link
                to={`/card-machines/${m.slug}`}
                onClick={() => analytics.productClicked(m.slug.replace(/-/g, "_"), { source: "card-machines-grid" })}
                className="group flex-1 flex flex-col"
                data-testid={`machine-details-${m.name.toLowerCase().replace(/\s+/g, '-')}`}
                data-measurement-owned="product"
              >
                <div className={`aspect-square rounded-xl mb-4 flex items-center justify-center overflow-hidden bg-white border border-brand-light-grey/50 ${m.cover ? "" : "p-5"}`}>
                  <img src={m.img} alt={m.name} className={`w-full h-full transition-transform duration-300 group-hover:scale-[1.03] ${m.cover ? "object-cover" : "object-contain"}`} />
                </div>
                <h3 className="font-heading text-lg font-bold text-brand-black group-hover:text-brand-aqua transition-colors mb-1">{m.name}</h3>
                <span className="text-xs font-semibold text-brand-aqua flex items-center gap-1 mb-3 group-hover:gap-2 transition-all">
                  View details <ArrowRight size={12} />
                </span>
                <ul className="space-y-2">
                  {m.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-brand-grey">
                      <CreditCard size={13} className="text-brand-aqua mt-0.5 flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
              </Link>
              {/* Route enquiries through the product page's own form section —
                  trust content precedes the form and the lead arrives tagged
                  (ScrollToTop handles the #enquire hash scroll). */}
              <Link
                to={`/card-machines/${m.slug}?product=${encodeURIComponent(m.name)}&category=card-machines&source=card-machines-grid#enquire`}
                className="mt-4 block text-center bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full py-2.5 text-sm transition-colors"
                data-testid={`machine-enquire-${m.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                Enquire about this machine
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

/**
 * Pricing-honesty banner. We deliberately don't publish list prices —
 * turn that into the pitch and route to the statement review.
 */
function PricingHonestyBanner() {
  return (
    <section className="py-14 bg-brand-aqua-light/40 border-y border-brand-light-grey/60" data-testid="card-machines-pricing-banner">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={fadeUp}>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-4">
            No list prices — a deal built on your numbers
          </h2>
          <p className="text-sm sm:text-base text-brand-grey leading-relaxed mb-8 max-w-2xl mx-auto">
            Card machine costs depend on your turnover and card mix, so a one-size price would short-change most businesses. Instead, we price against your actual statement — and beat your current rates where we can. It takes one upload to find out.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/statement-review"
              onClick={() => analytics.clickCta("card-machines-free-statement-review", "/statement-review")}
              className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors"
              data-testid="card-machines-cta-statement-review"
              data-measurement-owned="cta"
            >
              <FileSearch size={16} /> Get a free statement review
            </Link>
            <Link
              to="/pricing"
              onClick={() => analytics.clickCta("card-machines-how-pricing-works", "/pricing")}
              className="bg-white text-brand-black hover:text-brand-aqua border border-brand-light-grey font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors"
              data-testid="card-machines-cta-pricing"
              data-measurement-owned="cta"
            >
              How our pricing works
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function HubFAQSection() {
  return (
    <section className="py-16 md:py-20 bg-gray-50" data-testid="card-machines-faqs">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-brand-black tracking-tight mb-8 text-center">
          Card machine FAQs
        </h2>
        <ResponsiveAccordion items={HUB_FAQS} testIdPrefix="card-machines-faq" />
      </div>
    </section>
  );
}

export default function CardMachinesPage() {
  useProductViewed("card_machines");
  return (
    <div>
      <SEOHead
        title="Card Machines for Businesses in the UK"
        description="Looking for reliable card machines for UK businesses? CanDo Payments offers fast and secure affordable card payment terminals to help your business thrive."
        path="/card-machines"
        service={{ name: "Card Machines", description: "Countertop, portable, and mobile card terminals for UK businesses with next-day settlement, transparent pricing and UK-based support.", serviceType: "Payment Terminal Services" }}
        faqs={HUB_FAQS}
        schema={ITEM_LIST_SCHEMA}
      />
      <CardMachinesHero />

      {/* Why CanDo Card Machines */}
      <section className="py-16 bg-white border-b border-brand-light-grey/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            {[
              { icon: Wifi, label: "WiFi & 4G Options" },
              { icon: ShieldCheck, label: "PCI Compliant" },
              { icon: Smartphone, label: "Contactless Ready" },
              { icon: Zap, label: "Order by 2pm — live next working day" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl bg-brand-aqua-light">
                <item.icon size={20} className="text-brand-aqua flex-shrink-0" />
                <span className="text-sm font-semibold text-brand-black">{item.label}</span>
              </div>
            ))}
          </div>
          <SocialProofStrip reassurance="Real support from a family-run UK team" testId="card-machines-social-proof" />
        </div>
      </section>

      <MachineGrid />
      <PricingHonestyBanner />
      <ProductComparisonMatrix
        heading="Compare the range"
        subheading="Portable, countertop, smart Android or pocket reader — the honest side-by-side."
        testId="card-machines-comparison"
      />

      {/* Bottom text + CTA */}
      <section className="py-20 md:py-28 bg-white" data-testid="card-machines-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-6">
                Why choose CanDo for your card machines?
              </h2>
              <div className="space-y-4 text-sm leading-relaxed text-brand-grey">
                <p>Reliability matters. A card machine that drops connections or freezes at busy times costs sales and trust. CanDo Payments supplies terminals built for everyday trading — whether you run a shop, cafe, mobile business, or service provider.</p>
                <p>Pricing transparency is where many providers fall short. Headline rates can look attractive, but hidden fees often appear later. We focus on clear, upfront pricing so you know exactly what you&apos;re paying.</p>
                <p>When something goes wrong, quick access to knowledgeable help saves time and stress. Our UK-based support team understands card payments and real businesses — not scripted call centre responses.</p>
              </div>
            </div>
            <div className="space-y-5">
              <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
                <h3 className="font-heading text-xl font-bold text-brand-black mb-4">Find the right machine</h3>
                <p className="text-sm text-brand-grey mb-6">Tell us about your business and we&apos;ll recommend the best card machine and pricing for you.</p>
                <ContactForm />
              </div>
              <FormReviewCompanion product="card-machines" testId="card-machines-form-review" />
            </div>
          </div>
        </div>
      </section>

      <HubFAQSection />
      <LocationCrossLinks />
      <RelatedReviews
        product="card-machines"
        eyebrow="Customer Stories"
        heading="See what our customers say about our card machines"
        subheading="Real reviews from UK businesses using CanDo card machines every day."
        testId="card-machines-related-reviews"
      />
    </div>
  );
}
